import React, { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { useInView } from 'react-intersection-observer'
import css from './MemberGroups.module.css'
import listCss from '../UserList/UserList.module.css'
import itemCss from '../../atoms/UserListItem/UserListItem.module.css'
import UserList from '../UserList/UserList'
import ButtonToolbar from '../../buttons/ButtonToolbar/ButtonToolbar'
import LoadingSkeleton from '../../utility/LoadingSceleton/LoadingSkeleton'
import {
  useAllMemberViews,
  useMemberViewHistory,
} from '../../../hooks/useGymEngagement'
import useBatchedRows, { ROWS_PER_BATCH } from '../../../hooks/useBatchedRows'
import { GymMemberData } from '../../../types/types'
import { joinedAtMs, trackedAtMs } from '../../../utilities/memberDates'

type Views = { [uid: string]: number | null | undefined } | null | undefined

/** Latest of last view and last tracked */
const activeAt = (member: GymMemberData, views: Views) =>
  Math.max(views?.[member.uid] ?? 0, trackedAtMs(member))

/** Newest activity first, then by name */
const byActivity = (views: Views) => (a: GymMemberData, b: GymMemberData) =>
  activeAt(b, views) - activeAt(a, views) ||
  (a.name || '').localeCompare(b.name || '')

/**
 * Groups are calendar months counted back from this one (0 = this month,
 * 1 = last month, …), all the way down. Members without a date go last
 */
const NO_DATE = Number.MAX_SAFE_INTEGER

/** The month group a date (ms) falls in, or NO_DATE without one */
const monthGroupOf = (ms: number) => {
  if (!ms) return NO_DATE
  const thisMonth = DateTime.now().startOf('month')
  const month = DateTime.fromMillis(ms).startOf('month')
  return Math.max(0, Math.round(thisMonth.diff(month, 'months').months))
}

/** "October", or "December 2025" for a month in another year */
const monthTitle = (group: number) => {
  const month = DateTime.now().startOf('month').minus({ months: group })
  return month.hasSame(DateTime.now(), 'year')
    ? month.toFormat('LLLL')
    : month.toFormat('LLLL yyyy')
}

type ListProps = {
  onUserAction: (action: string, data: GymMemberData) => void
  lastOpenedAt?: { [uid: string]: number } | null
  activityIsLoading?: boolean
}

/**
 * Rows split into month groups, each with a heading and its own card, with
 * numbering running on across groups. `rows` must already be in group
 * order; empty months are skipped. `footer` (a loader or an error) goes at
 * the end of the last card. Without `isGrouped`, one card, no headings.
 */
const GroupedRows = ({
  rows,
  groupOf,
  noDateTitle,
  isGrouped,
  footer,
  ...listProps
}: ListProps & {
  rows: GymMemberData[]
  groupOf: (member: GymMemberData) => number
  /** Heading for members without a date, e.g. "Never tracked" */
  noDateTitle: string
  isGrouped: boolean
  footer?: React.ReactNode
}) => {
  const groups: { group: number; members: GymMemberData[] }[] = []
  rows.forEach((member) => {
    const group = isGrouped ? groupOf(member) : 0
    const last = groups[groups.length - 1]
    if (last && last.group === group) last.members.push(member)
    else groups.push({ group, members: [member] })
  })
  if (!groups.length) groups.push({ group: -1, members: [] })

  let startIndex = 1
  return (
    <>
      {groups.map(({ group, members }, index) => {
        const isLast = index === groups.length - 1
        const card = (
          <section key={group}>
            {members.length > 0 && (
              <UserList
                data={members}
                keepOrder
                startIndex={startIndex}
                {...listProps}
              />
            )}
            {isLast && footer}
          </section>
        )
        startIndex += members.length
        return isGrouped && group !== -1 ? (
          <React.Fragment key={group}>
            <h3
              className={css['subheading']}
              style={index === 0 ? { marginTop: 0 } : undefined}
            >
              {group === NO_DATE ? noDateTitle : monthTitle(group)}
            </h3>
            {card}
          </React.Fragment>
        ) : (
          card
        )
      })}
    </>
  )
}

type ActivityHistoryListProps = {
  gymID: string
  members: GymMemberData[]
  /** Split by month, with a heading per month */
  isGrouped: boolean
  onUserAction: (action: string, data: GymMemberData) => void
}

/**
 * Members by last activity (latest of last view and last tracked), newest
 * first. Views load one calendar month at a time as the list scrolls
 * (infinite scroll), starting with this month. Going back in time, a
 * member's place is the first month where they viewed or tracked, which is
 * also their month heading. Loading stops after a year or once everyone is
 * placed; members with no activity in that year go last, under their own
 * heading.
 *
 * Rows render 20 at a time, and the next month is only requested once every
 * loaded row is shown. Each month is a small PostHog query (~1s) instead of
 * one year-long query (~6s, near PostHog's 10s limit).
 */
export const ActivityHistoryList = ({
  gymID,
  members,
  isGrouped,
  onUserAction,
}: ActivityHistoryListProps) => {
  // Months end today: the first window is this month so far
  const [historyEnd] = useState(() =>
    DateTime.now().startOf('day').plus({ days: 1 }).toMillis()
  )
  // Nothing loads until the end of the list is in view
  const [hasStarted, setHasStarted] = useState(false)
  const [shownCount, setShownCount] = useState(ROWS_PER_BATCH)
  const { ref: endRef, inView: endIsInView } = useInView({
    rootMargin: '400px',
  })

  const history = useMemberViewHistory(gymID, historyEnd, hasStarted)

  // Order members by the newest month with a view or a workout
  const placedUIDs = new Set<string>()
  const views: { [uid: string]: number } = {}
  const ordered: GymMemberData[] = []
  const groupByUID: { [uid: string]: number } = {}

  history.data?.pages.forEach(({ window, lastViewedAt }, pageIndex) => {
    // Pages go newest to oldest, so keep the first (latest) view per member
    Object.entries(lastViewedAt || {}).forEach(([uid, at]) => {
      if (!(uid in views)) views[uid] = at
    })
    const monthMembers = members
      .filter((m) => {
        if (placedUIDs.has(m.uid)) return false
        const tracked = trackedAtMs(m)
        return (
          !!lastViewedAt?.[m.uid] ||
          (tracked >= window.from && tracked < window.to)
        )
      })
      .sort(byActivity(lastViewedAt))
    monthMembers.forEach((m) => {
      placedUIDs.add(m.uid)
      // Pages are calendar months from this one back
      groupByUID[m.uid] = pageIndex
    })
    ordered.push(...monthMembers)
  })

  const unplaced = members.filter((m) => !placedUIDs.has(m.uid))
  const isDone = !unplaced.length || (!!history.data && !history.hasNextPage)
  const canLoadMore = !isDone && !history.isError

  // No activity in the loaded months: last, once everything has loaded
  if (isDone) ordered.push(...unplaced.sort(byActivity(views)))

  const shown = ordered.slice(0, shownCount)
  const hasHiddenRows = shown.length < ordered.length

  const { fetchNextPage, isFetching } = history
  useEffect(() => {
    if (!endIsInView) return
    if (hasHiddenRows) setShownCount((count) => count + ROWS_PER_BATCH)
    else if (!canLoadMore) return
    else if (!hasStarted) setHasStarted(true)
    else if (!isFetching) fetchNextPage()
    // Re-checks after every batch and page, so it keeps going while the end
    // is visible (including through months with nobody in them)
  }, [
    endIsInView,
    hasHiddenRows,
    canLoadMore,
    hasStarted,
    isFetching,
    fetchNextPage,
    shownCount,
  ])

  return (
    <GroupedRows
      rows={shown}
      groupOf={(member) => groupByUID[member.uid] ?? NO_DATE}
      noDateTitle="No activity in the past year"
      isGrouped={isGrouped}
      onUserAction={onUserAction}
      lastOpenedAt={views}
      footer={
        history.isError ? (
          <div className={css['load-error']}>
            <span>Couldn’t load earlier activity.</span>
            <ButtonToolbar type="light" onClick={() => history.fetchNextPage()}>
              Try again
            </ButtonToolbar>
          </div>
        ) : (
          (hasHiddenRows || canLoadMore) && (
            // Scrolling this into view shows more rows or loads the next month
            <div
              ref={endRef}
              className={css['load-more']}
              style={shown.length ? undefined : { marginTop: 0 }}
            >
              <MemberRowsSkeleton rows={3} startIndex={shown.length + 1} />
            </div>
          )
        )
      }
    />
  )
}

export type MemberSort = 'viewed' | 'tracked' | 'joined'

/**
 * Newest tracked or newest joined first, then by name, rendered 20 rows at a
 * time. Grouped like ActivityHistoryList by the month of that date, with
 * members who never tracked (or have no join date) last. With `gymID`, rows also show last views, which load
 * in the background month by month (any row can need any month); each row's
 * dates show once its view is found or every month has loaded.
 */
export const SortedMemberList = ({
  gymID,
  members,
  sort,
  isGrouped,
  onUserAction,
}: {
  /** Null for staff who can't see the stats: no view dates */
  gymID: string | null
  members: GymMemberData[]
  sort: 'tracked' | 'joined'
  isGrouped: boolean
  onUserAction: (action: string, data: GymMemberData) => void
}) => {
  const dateOf = sort === 'tracked' ? trackedAtMs : joinedAtMs
  const sorted = [...members].sort(
    (a, b) =>
      dateOf(b) - dateOf(a) || (a.name || '').localeCompare(b.name || '')
  )
  const { views, isComplete: viewsAreComplete } = useAllMemberViews(
    gymID,
    members.map((m) => m.uid),
    !!gymID
  )
  const { shown, isComplete, endRef } = useBatchedRows(sorted)

  return (
    <GroupedRows
      rows={shown}
      groupOf={(member) => monthGroupOf(dateOf(member))}
      noDateTitle={sort === 'tracked' ? 'Never tracked' : 'Join date unknown'}
      isGrouped={isGrouped}
      onUserAction={onUserAction}
      lastOpenedAt={gymID ? views : undefined}
      activityIsLoading={!!gymID && !viewsAreComplete}
      footer={
        !isComplete && (
          // Scrolling this into view renders the next batch
          <div ref={endRef} className={css['load-more']}>
            <MemberRowsSkeleton rows={3} startIndex={shown.length + 1} />
          </div>
        )
      }
    />
  )
}

// Fixed widths so the skeleton doesn't jump between renders
const SKELETON_WIDTHS = [160, 128, 184, 144, 120]

/**
 * Placeholder rows built from the same classes as UserList/UserListItem,
 * so numbers, avatars and text line up with the real rows
 */
export const MemberRowsSkeleton = ({
  rows,
  startIndex,
}: {
  rows: number
  startIndex: number
}) => (
  <div
    className={listCss['user-list']}
    aria-busy="true"
    aria-label="Loading members"
  >
    <ul>
      {SKELETON_WIDTHS.slice(0, rows).map((width, index) => (
        <li
          key={index}
          className={itemCss['li']}
          style={{ pointerEvents: 'none' }}
        >
          <div className={itemCss['left']}>
            <div className={itemCss['index']}>{startIndex + index}</div>
            <LoadingSkeleton
              type="block"
              style={{ width: '56px', height: '56px', borderRadius: '50%' }}
            />
            <div className={itemCss['text-info']}>
              {/* Bars centered in the name (24px) and detail (20px) lines */}
              <LoadingSkeleton
                type="block"
                style={{
                  width: `${width}px`,
                  height: '16px',
                  margin: '4px 0',
                  borderRadius: '4px',
                }}
              />
              <LoadingSkeleton
                type="block"
                style={{
                  width: '220px',
                  height: '12px',
                  // 4px gap below the name, then centered in its 20px line
                  margin: '8px 0 4px',
                  borderRadius: '4px',
                }}
              />
            </div>
          </div>
        </li>
      ))}
    </ul>
  </div>
)
